import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('hotelflow_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, orgCode) => {
    const response = await axios.post(`${API}/auth/login`, { username, password, org_code: orgCode || null });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('hotelflow_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register-organization`, data);
    const { token: newToken, user: userData } = response.data;
    // Pending organizations get no token — don't establish a session
    if (newToken) {
      localStorage.setItem('hotelflow_token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
    }
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('hotelflow_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const toggleShift = async () => {
    if (!user) return;
    const response = await axios.put(`${API}/users/${user.id}/shift`);
    setUser({ ...user, on_shift: response.data.on_shift });
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, toggleShift, isAdmin: user?.role === 'admin', isSuperadmin: user?.role === 'superadmin' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
